import { Box, ListItem, Flex, HStack, Switch, Text } from '@chakra-ui/react';
import type { SiteLocationType, SiteRegionType } from './SiteList';
import { VirtualItem } from '@tanstack/react-virtual';
import { useCallback, useEffect, useRef, useState } from 'react';

export function VirtualListItem({
  virtualRow,
  site,
  region,
  measureElement,
  toggleSiteAvailability,
  isLastItem,
}: {
  virtualRow: VirtualItem;
  site: SiteLocationType;
  region?: SiteRegionType | undefined;
  measureElement: (el: HTMLElement | null) => void;
  toggleSiteAvailability: (site: SiteLocationType) => void;
  isLastItem: boolean;
}) {
  const [isChecked, setIsChecked] = useState(site.assigned);
  const [isCooldown, setIsCooldown] = useState(false);

  const cooldownTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  // The list virtualises, so a row unmounts as soon as it scrolls out of view,
  // which can easily be inside the cooldown. Drop the timer with the row rather
  // than let it wake up and set state on something that is gone.
  useEffect(
    () => () => {
      clearTimeout(cooldownTimer.current);
    },
    [],
  );

  const handleToggle = useCallback(() => {
    setIsChecked(!isChecked);
    toggleSiteAvailability(site);
    // Disable the switch to prevent spamming
    setIsCooldown(true);
    cooldownTimer.current = setTimeout(() => {
      setIsCooldown(false);
    }, 800);
  }, [setIsCooldown, isChecked, toggleSiteAvailability, site]);

  let isDisabled = isCooldown;

  if (isDisabled === false && region) {
    isDisabled = region.is_default;
  }

  return (
    <ListItem
      ref={measureElement}
      data-index={virtualRow.index}
      borderLeft="1px solid"
      borderRight="1px solid"
      borderTop="1px solid"
      borderBottom={isLastItem ? '1px solid' : 'transparent'}
      borderTopRadius={virtualRow.index === 0 ? 'lg' : '0'}
      borderBottomRadius={isLastItem ? 'lg' : '0'}
      borderColor="gray.100"
      p={3}
    >
      <Flex align={'center'} justify="space-between" w="full">
        <HStack>
          <Box as={'span'}>
            <Text fontWeight="medium">
              {site.name}
              {', '}
            </Text>
          </Box>
          {site.state ? (
            <Box as={'span'}>
              <Text color="fg.subtle">{site.state}</Text>
            </Box>
          ) : null}
        </HStack>
        <HStack>
          {site.type && site.type === 'region' ? (
            <Box as={'span'}>
              <Text fontSize="xs" color="fg.subtle">
                {site?.count} Sites
              </Text>
            </Box>
          ) : null}
          {region && region.is_default ? (
            isChecked ? (
              <Switch
                name="available"
                colorScheme="brand"
                isChecked={isChecked}
                isDisabled={isChecked}
              />
            ) : null
          ) : (
            <Switch
              name="available"
              onChange={handleToggle}
              colorScheme="brand"
              isChecked={isChecked}
              isDisabled={isDisabled}
            />
          )}
        </HStack>
      </Flex>
    </ListItem>
  );
}
